import { type FC, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import {
  Drawer,
  EmptyState,
  FilterBar,
  LoadingState,
  SearchInput,
  useToast,
} from '@/components/base';
import { useLegalProducts } from '@/hooks/useLegalProducts';
import {
  type CreateKnowledgeItemInput,
  type KnowledgeItem,
  useKnowledgeItems,
} from '@/hooks/useKnowledgeItems';

type KnowledgeItemFormState = {
  title: string;
  category: string;
  legalArea: string;
  legalProductId: string;
  content: string;
  tags: string;
  source: string;
  notes: string;
};

const knowledgeCategories = [
  { label: 'Tese', value: 'tese' },
  { label: 'Fundamento', value: 'fundamento' },
  { label: 'Jurisprudência', value: 'jurisprudencia' },
  { label: 'Checklist', value: 'checklist' },
  { label: 'Modelo', value: 'modelo' },
  { label: 'Estratégia', value: 'estrategia' },
  { label: 'Operacional', value: 'operacional' },
  { label: 'Outro', value: 'outro' },
];

const knowledgeAreas = [
  { label: 'Militar', value: 'militar' },
  { label: 'Criminal', value: 'criminal' },
  { label: 'Cível', value: 'civel' },
  { label: 'Consumidor', value: 'consumidor' },
  { label: 'Bancário', value: 'bancario' },
  { label: 'Saúde', value: 'saude' },
  { label: 'Securitário', value: 'securitario' },
  { label: 'Administrativo', value: 'administrativo' },
  { label: 'Previdenciário', value: 'previdenciario' },
  { label: 'Trabalhista', value: 'trabalhista' },
  { label: 'Outro', value: 'outro' },
];

const dateTimeFormatter = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
});

const toNullableText = (value: string) => {
  const text = value.trim();
  return text === '' ? null : text;
};

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const emptyFormState = (): KnowledgeItemFormState => ({
  title: '',
  category: 'outro',
  legalArea: 'outro',
  legalProductId: '',
  content: '',
  tags: '',
  source: '',
  notes: '',
});

const knowledgeItemToFormState = (item: KnowledgeItem): KnowledgeItemFormState => ({
  title: item.title,
  category: item.category ?? 'outro',
  legalArea: item.legalArea ?? 'outro',
  legalProductId: item.legalProductId ?? '',
  content: item.content,
  tags: item.tags ?? '',
  source: item.source ?? '',
  notes: item.notes ?? '',
});

const formatCategoryLabel = (value: string | null) =>
  knowledgeCategories.find((option) => option.value === value)?.label ?? value ?? 'Outro';

const formatAreaLabel = (value: string | null) =>
  knowledgeAreas.find((option) => option.value === value)?.label ?? value ?? 'Outro';

const formatDateTime = (value: string) => {
  try {
    return dateTimeFormatter.format(new Date(value));
  } catch {
    return value;
  }
};

const formatTags = (value: string | null) =>
  (value ?? '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

const KnowledgePage: FC = () => {
  const { canManageLegalStructures } = useRolePermissions();
  const { addToast } = useToast();
  const { knowledgeItems, loading, error, refetch, createKnowledgeItem, updateKnowledgeItem, archiveKnowledgeItem } =
    useKnowledgeItems();
  const { legalProducts } = useLegalProducts();

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [showDetail, setShowDetail] = useState<KnowledgeItem | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingItem, setEditingItem] = useState<KnowledgeItem | null>(null);
  const [form, setForm] = useState<KnowledgeItemFormState>(emptyFormState());
  const [isSavingKnowledgeItem, setIsSavingKnowledgeItem] = useState(false);
  const [archivingKnowledgeItemId, setArchivingKnowledgeItemId] = useState('');

  const filteredKnowledgeItems = useMemo(() => {
    const query = normalizeSearchText(search);

    return knowledgeItems.filter((item) => {
      if (categoryFilter && item.category !== categoryFilter) {
        return false;
      }

      if (areaFilter && item.legalArea !== areaFilter) {
        return false;
      }

      if (productFilter && item.legalProductId !== productFilter) {
        return false;
      }

      if (!query) {
        return true;
      }

      const haystack = normalizeSearchText(
        [
          item.title,
          item.category,
          item.legalArea,
          item.legalProductName,
          item.tags,
          item.source,
          item.notes,
          item.content,
        ]
          .filter(Boolean)
          .join(' ')
      );

      return haystack.includes(query);
    });
  }, [areaFilter, categoryFilter, knowledgeItems, productFilter, search]);

  const legalProductOptions = useMemo(
    () => legalProducts.map((product) => ({ label: product.name, value: product.id })),
    [legalProducts]
  );

  const getLegalProductName = (item: KnowledgeItem) =>
    item.legalProductName ||
    legalProducts.find((product) => product.id === item.legalProductId)?.name ||
    'Sem vínculo';

  const openCreateForm = () => {
    if (isSavingKnowledgeItem) {
      return;
    }

    setShowDetail(null);
    setEditingItem(null);
    setForm(emptyFormState());
    setShowEditor(true);
  };

  const openEditForm = (item: KnowledgeItem) => {
    if (isSavingKnowledgeItem) {
      return;
    }

    setShowDetail(null);
    setEditingItem(item);
    setForm(knowledgeItemToFormState(item));
    setShowEditor(true);
  };

  const closeEditor = () => {
    if (isSavingKnowledgeItem) {
      return;
    }

    setShowEditor(false);
    setEditingItem(null);
    setForm(emptyFormState());
  };

  const closeDetail = () => {
    setShowDetail(null);
  };

  const buildPayload = (): CreateKnowledgeItemInput => ({
    title: form.title.trim(),
    category: toNullableText(form.category),
    legalArea: toNullableText(form.legalArea),
    legalProductId: toNullableText(form.legalProductId),
    content: form.content.trim(),
    tags: toNullableText(form.tags),
    source: toNullableText(form.source),
    notes: toNullableText(form.notes),
    isActive: editingItem?.isActive ?? true,
  });

  const handleSave = async () => {
    if (isSavingKnowledgeItem) {
      return;
    }

    if (!form.title.trim()) {
      addToast('Preencha o título do item de conhecimento.', 'warning');
      return;
    }

    if (!form.content.trim()) {
      addToast('Preencha o conteúdo do item de conhecimento.', 'warning');
      return;
    }

    setIsSavingKnowledgeItem(true);
    const currentEditingId = editingItem?.id ?? null;

    try {
      const payload = buildPayload();

      if (editingItem) {
        await updateKnowledgeItem(editingItem.id, payload);
        addToast('Item de conhecimento atualizado com sucesso.', 'success');
      } else {
        await createKnowledgeItem(payload);
        addToast('Item de conhecimento criado com sucesso.', 'success');
      }

      await refetch();
      setShowEditor(false);
      setEditingItem(null);
      setForm(emptyFormState());

      if (currentEditingId && showDetail?.id === currentEditingId) {
        setShowDetail(null);
      }
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível salvar o item de conhecimento.', 'error');
    } finally {
      setIsSavingKnowledgeItem(false);
    }
  };

  const handleArchive = async (item: KnowledgeItem) => {
    if (isSavingKnowledgeItem || archivingKnowledgeItemId) {
      return;
    }

    const confirmed = window.confirm(
      'Tem certeza que deseja arquivar este item de conhecimento? Esta ação removerá o item da listagem principal, mas manterá o registro no banco para auditoria.'
    );

    if (!confirmed) {
      return;
    }

    setArchivingKnowledgeItemId(item.id);

    try {
      await archiveKnowledgeItem(item.id);
      await refetch();
      addToast('Item de conhecimento arquivado com sucesso.', 'success');

      if (editingItem?.id === item.id) {
        setShowEditor(false);
        setEditingItem(null);
        setForm(emptyFormState());
      }

      if (showDetail?.id === item.id) {
        setShowDetail(null);
      }
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível arquivar o item de conhecimento.', 'error');
    } finally {
      setArchivingKnowledgeItemId('');
    }
  };

  const hasItems = filteredKnowledgeItems.length > 0;

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">

        {!canManageLegalStructures ? (
          <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
            Modo consulta: seu perfil pode visualizar este módulo, mas alterações estruturais são reservadas a administradores e advogados.
          </div>
        ) : null}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-foreground-900">Biblioteca de Conhecimento</h2>
            <p className="text-sm text-foreground-500 mt-0.5">
              {filteredKnowledgeItems.length} itens internos no acervo
            </p>
          </div>
          <button
            hidden={!canManageLegalStructures}
            onClick={openCreateForm}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-background-50 rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i> Novo Item
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Buscar na biblioteca..."
            className="lg:w-80"
          />
          <FilterBar
            filters={[
              {
                key: 'category',
                label: 'Categoria',
                options: knowledgeCategories.map((option) => ({
                  label: option.label,
                  value: option.value,
                })),
                value: categoryFilter,
                onChange: setCategoryFilter,
              },
              {
                key: 'legalArea',
                label: 'Área',
                options: knowledgeAreas.map((option) => ({
                  label: option.label,
                  value: option.value,
                })),
                value: areaFilter,
                onChange: setAreaFilter,
              },
              {
                key: 'legalProductId',
                label: 'Produto',
                options: legalProductOptions,
                value: productFilter,
                onChange: setProductFilter,
              },
            ]}
          />
        </div>

        {loading && <LoadingState message="Carregando biblioteca de conhecimento..." />}

        {error && !loading && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center justify-between gap-3">
            <span>{error}</span>
            <button
              onClick={() => void refetch()}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {!loading && !hasItems && (
          <EmptyState
            icon="ri-book-open-line"
            title="Nenhum item encontrado"
            description="Crie o primeiro item da biblioteca para registrar teses, checklists e orientações internas."
            action={canManageLegalStructures ? {
              label: 'Novo Item',
              onClick: openCreateForm,
              icon: 'ri-add-line',
            } : undefined}
          />
        )}

        {!loading && hasItems && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filteredKnowledgeItems.map((item) => {
              const tags = formatTags(item.tags);
              const preview = item.content.replace(/\s+/g, ' ').trim();
              const summary = preview.length > 180 ? `${preview.slice(0, 177)}...` : preview;

              return (
                <div key={item.id} className="bg-background-50 border border-background-200 rounded-xl p-4 flex flex-col">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 font-medium whitespace-nowrap">
                        {formatCategoryLabel(item.category)}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-200 font-medium whitespace-nowrap">
                        {formatAreaLabel(item.legalArea)}
                      </span>
                      {item.isActive ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium whitespace-nowrap">
                          Ativo
                        </span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-foreground-100 text-foreground-500 border border-background-200 font-medium whitespace-nowrap">
                          Arquivado
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-foreground-400 whitespace-nowrap">
                      {formatDateTime(item.updatedAt)}
                    </span>
                  </div>

                  <button
                    onClick={() => setShowDetail(item)}
                    className="text-left text-sm font-semibold text-foreground-900 hover:text-primary-700 transition-colors"
                  >
                    {item.title}
                  </button>

                  <div className="mt-2 space-y-1 text-xs text-foreground-500">
                    <p>
                      <span className="font-medium text-foreground-600">Produto:</span>{' '}
                      {getLegalProductName(item)}
                    </p>
                    {item.source && (
                      <p>
                        <span className="font-medium text-foreground-600">Fonte:</span> {item.source}
                      </p>
                    )}
                  </div>

                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed flex-1">{summary}</p>

                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-1.5 py-0.5 rounded text-[10px] bg-accent-50 text-accent-700 font-medium whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-background-100">
                    <button
                      onClick={() => setShowDetail(item)}
                      className="text-xs text-primary-600 font-medium hover:text-primary-700 cursor-pointer whitespace-nowrap"
                    >
                      Ver detalhes
                    </button>
                    <button
            hidden={!canManageLegalStructures}
                      onClick={() => openEditForm(item)}
                      className="text-xs text-foreground-400 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                    >
                      Editar
                    </button>
                    <button
            hidden={!canManageLegalStructures}
                      onClick={() => void handleArchive(item)}
                      disabled={archivingKnowledgeItemId === item.id}
                      className="text-xs text-foreground-400 hover:text-red-500 cursor-pointer whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {archivingKnowledgeItemId === item.id ? 'Arquivando...' : 'Arquivar'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Drawer
          isOpen={!!showDetail}
          onClose={closeDetail}
          title={showDetail?.title || 'Detalhe do item'}
          footer={
            <div className="flex items-center gap-2 flex-wrap">
              <button
            hidden={!canManageLegalStructures}
                onClick={() => showDetail && openEditForm(showDetail)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-edit-line mr-1"></i>Editar
              </button>
              <button
            hidden={!canManageLegalStructures}
                onClick={() => showDetail && void handleArchive(showDetail)}
                disabled={archivingKnowledgeItemId === showDetail?.id}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 text-red-700 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className="ri-archive-line mr-1"></i>
                {archivingKnowledgeItemId === showDetail?.id ? 'Arquivando...' : 'Arquivar'}
              </button>
            </div>
          }
        >
          {showDetail && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 font-medium whitespace-nowrap">
                  {formatCategoryLabel(showDetail.category)}
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-200 font-medium whitespace-nowrap">
                  {formatAreaLabel(showDetail.legalArea)}
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent-50 text-accent-700 border border-accent-200 font-medium whitespace-nowrap">
                  {getLegalProductName(showDetail)}
                </span>
                {showDetail.isActive ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium whitespace-nowrap">
                    Ativo
                  </span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-foreground-100 text-foreground-500 border border-background-200 font-medium whitespace-nowrap">
                    Arquivado
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Título</p>
                  <p className="text-sm text-foreground-700">{showDetail.title}</p>
                </div>
                <div>
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Fonte</p>
                  <p className="text-sm text-foreground-700">{showDetail.source || '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Criado em</p>
                  <p className="text-sm text-foreground-700">{formatDateTime(showDetail.createdAt)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Atualizado em</p>
                  <p className="text-sm text-foreground-700">{formatDateTime(showDetail.updatedAt)}</p>
                </div>
              </div>

              {formatTags(showDetail.tags).length > 0 && (
                <div>
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider mb-1">Tags</p>
                  <div className="flex flex-wrap gap-1">
                    {formatTags(showDetail.tags).map((tag) => (
                      <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-accent-50 text-accent-700 font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {showDetail.notes && (
                <div>
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider mb-1">Observações</p>
                  <p className="text-sm text-foreground-700 whitespace-pre-wrap">{showDetail.notes}</p>
                </div>
              )}

              <div>
                <p className="text-[10px] text-foreground-400 uppercase tracking-wider mb-1">Conteúdo</p>
                <div className="p-3 bg-background-100 rounded-lg text-sm text-foreground-700 whitespace-pre-wrap max-h-80 overflow-y-auto">
                  {showDetail.content}
                </div>
              </div>
            </div>
          )}
        </Drawer>

        <Drawer
          isOpen={canManageLegalStructures && showEditor}
          onClose={closeEditor}
          title={editingItem ? 'Editar Item' : 'Novo Item'}
          width="w-[720px]"
          footer={
            <div className="flex items-center gap-2">
              <button
                onClick={closeEditor}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
            hidden={!canManageLegalStructures}
                onClick={() => void handleSave()}
                disabled={isSavingKnowledgeItem}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSavingKnowledgeItem
                  ? 'Salvando...'
                  : editingItem
                    ? 'Salvar Alterações'
                    : 'Criar Item'}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-foreground-600 mb-1">Título *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Categoria</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {knowledgeCategories.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Área jurídica</label>
                <select
                  value={form.legalArea}
                  onChange={(e) => setForm((prev) => ({ ...prev, legalArea: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {knowledgeAreas.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-foreground-600 mb-1">Produto jurídico</label>
                <select
                  value={form.legalProductId}
                  onChange={(e) => setForm((prev) => ({ ...prev, legalProductId: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Sem vínculo</option>
                  {legalProductOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-foreground-600 mb-1">Conteúdo *</label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
                  rows={9}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-foreground-600 mb-1">Tags (separadas por vírgula)</label>
                <input
                  type="text"
                  value={form.tags}
                  onChange={(e) => setForm((prev) => ({ ...prev, tags: e.target.value }))}
                  placeholder="Ex: triagem, documentos, urgência"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-foreground-600 mb-1">Fonte</label>
                <input
                  type="text"
                  value={form.source}
                  onChange={(e) => setForm((prev) => ({ ...prev, source: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-foreground-600 mb-1">Observações</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>
          </div>
        </Drawer>
      </div>
    </AppLayout>
  );
};

export default KnowledgePage;
